import api from "../../api/api";
import { V2_BASE_URL } from "../../api/bases";
import { getPracticeId } from "../../config/practice";
import { unwrap } from "../staff/formwizApi";

/*
 * Internal staff forms + the patient's forms language (v2 gateway,
 * /v1/{practiceid}/formwiz/internal/* and /patients/{id}/forms-language).
 *
 * Pinned to v2 like staff/formwizApi.js (the routes exist on v2 only). A 404
 * (server without these routes, or FORMWIZ_INTERNAL=0) or 503
 * {code:"staffforms_not_installed"} (tables not created yet) means "not
 * available": callers hide the staff-form entry points quietly.
 */
const V2 = { baseURL: V2_BASE_URL, _gw: "v2-pinned" };
// api.js defaults to JSON: without this header axios serialises the FormData
// to "{}" (the Send Fax bug, memory 09-29).
const MULTIPART = { ...V2, headers: { "Content-Type": "multipart/form-data" } };

// The patient of a submission detail. GET /submissions/{id} and
// /internal/submissions/{id} carry it in `request.patientid` (SubmissionSummary
// has no top-level patientid); newer servers also send it at the top level.
export const patientIdOf = (detail) =>
  (detail && (detail.patientid || (detail.request && detail.request.patientid))) || null;

const base = () => `/v1/${getPracticeId()}/formwiz`;
const enc = (s) => encodeURIComponent(String(s));
const withParams = (params) => {
  const clean = {};
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "" && v !== false) clean[k] = v === true ? 1 : v;
  });
  return Object.keys(clean).length ? { ...V2, params: clean } : V2;
};

export const internalApi = {
  // Internal forms offered to this practice (?all=1: admins, disabled ones too).
  catalog: (all) => api.get(`${base()}/internal/catalog`, withParams({ all: all ? 1 : undefined })).then(unwrap),
  saveFormConfig: (formId, body) => api.put(`${base()}/internal/config/forms/${enc(formId)}`, body, V2).then(unwrap),

  // body {patientid, form_id, appointmentid?, note?, lang?, force?} -> InternalDetail (draft)
  create: (body) => api.post(`${base()}/internal/submissions`, body, V2).then(unwrap),
  get: (id) => api.get(`${base()}/internal/submissions/${enc(id)}`, V2).then(unwrap),
  // body {answers, step?, lang?, edit_seq} -> {edit_seq, saved_at}
  saveDraft: (id, body) => api.put(`${base()}/internal/submissions/${enc(id)}/draft`, body, V2).then(unwrap),
  // payload {answers, edit_seq, lang, completedAt?}; pdfBlob rendered from exactly payload.answers
  save: (id, payload, pdfBlob) => {
    const fd = new FormData();
    fd.append("payload", JSON.stringify(payload));
    if (pdfBlob) fd.append("pdf", pdfBlob, `internal-${id}.pdf`);
    return api.post(`${base()}/internal/submissions/${enc(id)}/save`, fd, MULTIPART).then(unwrap);
  },
  discard: (id, body) => api.post(`${base()}/internal/submissions/${enc(id)}/discard`, body || {}, V2).then(unwrap),
  voidForm: (id, body) => api.post(`${base()}/internal/submissions/${enc(id)}/void`, body, V2).then(unwrap),
  amend: (id, body) => api.post(`${base()}/internal/submissions/${enc(id)}/amend`, body, V2).then(unwrap),
  patientList: (patientid) => api.get(`${base()}/internal/patients/${enc(patientid)}`, V2).then(unwrap),
  // params {mine?, page?, page_size?}
  drafts: (params) => api.get(`${base()}/internal/drafts`, withParams(params)).then(unwrap),

  // The patient's forms language (saved default, chart, suggestion).
  language: (patientid) => api.get(`${base()}/patients/${enc(patientid)}/forms-language`, V2).then(unwrap),
  setLanguage: (patientid, language) => api.put(`${base()}/patients/${enc(patientid)}/forms-language`, { language }, V2).then(unwrap),
};

export default internalApi;
